import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  CloseIcon,
  ModalBody,
  Modal as ModalContainer,
  ModalContent,
  ModalHeader,
  Icon as ModalIcon,
  ModalTitle,
  SecondaryHeaderItem as SecondaryItem,
  FloatingCloseButton,
  FloatingCloseIcon,
  SecondaryHeaderItemPortal,
} from './modal-style';

const modalRoot = document.getElementById('modal');

/**
 * Modal Component
 * @param {object} props - props
 * @param {boolean} props.open - open
 * @param {Function} props.close - close
 * @param {string} props.title - title
 * @param {node} props.children - children
 * @param {boolean} props.showClose - showClose
 * @param {node} props.SecondaryHeaderItem - SecondaryHeaderItem
 * @param {string} props.height - height
 * @param {string} props.minHeight - minHeight
 * @param {string} props.width - width
 * @param {string} props.padding - padding
 * @param {boolean} props.hideModal - hideModal
 * @param {boolean} props.hideHeader - hideHeader
 * @param {boolean} props.showFloatingCloseButton - showFloatingCloseButton
 * @param {string} props.cy - cy
 * @param {string} props.titleFontSize - titleFontSize
 * @param {object} props.bodyStyle - bodyStyle
 * @param {string} props.alignItems - alignItems
 * @param {string} props.createPortal - createPortal
 * @param {string} props.headerPadding - headerPadding
 * @returns {JSX.Element}
 */
const Modal = ({
  open,
  close,
  title,
  children,
  showClose,
  SecondaryHeaderItem,
  height,
  width,
  padding,
  hideModal,
  cy,
  titleFontSize,
  bodyStyle,
  alignItems,
  minHeight,
  hideHeader,
  showFloatingCloseButton,
  createPortal,
  headerPadding,
}) => {
  const closeOnEscapeKeyDown = useCallback(
    (e) => {
      if ((e.charCode || e.keyCode) === 27) {
        close();
      }
    },
    [close],
  );

  useEffect(() => {
    document.body.addEventListener('keydown', closeOnEscapeKeyDown);
    return function cleanup() {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, [close, closeOnEscapeKeyDown]);

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <AnimatePresence>
      <ModalContainer
        data-cy={cy}
        key="modalContainer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        hideModal={hideModal}
      >
        <ModalContent
          width={width}
          $padding={padding}
          height={height}
          $minHeight={minHeight}
          $alignItems={alignItems}
          key="modalContent"
          initial={{ y: '100%' }}
          animate={{ y: 0, x: 0 }}
          exit={{ y: '-100%' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {!hideHeader && (
            <ModalHeader $headerPadding={headerPadding}>
              <ModalTitle
                data-cy="divModalTitle"
                $titleFontSize={titleFontSize}
              >
                {title}
              </ModalTitle>
              {SecondaryHeaderItem && (
                <SecondaryItem>{SecondaryHeaderItem}</SecondaryItem>
              )}
              {createPortal && <SecondaryHeaderItemPortal id={createPortal} />}
              {showClose && (
                <ModalIcon>
                  <CloseIcon
                    className="close-icon"
                    data-cy="icoCloseModal"
                    onClick={close}
                    size={24}
                  />
                </ModalIcon>
              )}
            </ModalHeader>
          )}
          <ModalBody style={bodyStyle}>
            {showFloatingCloseButton && (
              <FloatingCloseButton>
                <FloatingCloseIcon
                  data-cy="icoCloseModal"
                  onClick={close}
                  size={24}
                />
              </FloatingCloseButton>
            )}
            {children}
          </ModalBody>
        </ModalContent>
      </ModalContainer>
    </AnimatePresence>,
    modalRoot,
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  close: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  showClose: PropTypes.bool,
  SecondaryHeaderItem: PropTypes.node,
  height: PropTypes.string,
  minHeight: PropTypes.string,
  width: PropTypes.string,
  hideModal: PropTypes.bool,
  cy: PropTypes.string,
  titleFontSize: PropTypes.string,
  bodyStyle: PropTypes.object,
  alignItems: PropTypes.string,
  padding: PropTypes.string,
  hideHeader: PropTypes.bool,
  showFloatingCloseButton: PropTypes.bool,
  createPortal: PropTypes.string,
  headerPadding: PropTypes.string,
};

Modal.defaultProps = {
  showClose: true,
  SecondaryHeaderItem: null,
  title: '',
  width: '',
  height: '',
  minHeight: '100px',
  hideModal: false,
  cy: undefined,
  titleFontSize: undefined,
  bodyStyle: undefined,
  alignItems: null,
  padding: '0px',
  hideHeader: false,
  showFloatingCloseButton: false,
  createPortal: null,
  headerPadding: undefined,
};

export default Modal;
